import type { FC } from "react";
import { useMemo, useState } from "react";

interface InventoryStepProps {
  roomNumber: string;
  items: Array<{ id: number; label: string; sortOrder: number }>;
  isSubmitting: boolean;
  error: string;
  onCancel: () => void;
  onSubmit: (missingItemIds: number[]) => void;
}

const InventoryStep: FC<InventoryStepProps> = ({
  roomNumber,
  items,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}) => {
  const sorted = useMemo(
    () => [...items].sort((a, b) => a.sortOrder - b.sortOrder),
    [items],
  );

  const [present, setPresent] = useState<Set<number>>(() => new Set());
  const [pendingMissing, setPendingMissing] = useState<number[] | null>(null);

  const togglePresent = (id: number) => {
    setPresent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCheckOut = () => {
    const missing = sorted
      .filter((it) => !present.has(it.id))
      .map((it) => it.id);
    if (missing.length === 0) {
      onSubmit([]);
      return;
    }
    setPendingMissing(missing);
  };

  const handleConfirmMissing = () => {
    if (pendingMissing) {
      onSubmit(pendingMissing);
    }
  };

  const handleBackFromConfirm = () => {
    setPendingMissing(null);
  };

  return (
    <div className="bc-fade-up flex w-full max-w-md flex-col">
      <header className="px-6 pb-4 pt-10 text-center sm:pt-14">
        <h1 className="font-oswald text-3xl font-medium uppercase tracking-wide text-white sm:text-4xl">
          Final Walkthrough
        </h1>
        <p className="mt-2 text-sm text-white/40">Room {roomNumber}</p>
        <p className="mt-3 text-sm text-white/60">
          Tap each item to confirm it is in the room.
        </p>
      </header>

      <div className="space-y-2 px-6 pb-4">
        {sorted.map((item) => {
          const isPresent = present.has(item.id);
          return (
            <button
              type="button"
              key={item.id}
              onClick={() => togglePresent(item.id)}
              aria-pressed={isPresent}
              className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all active:scale-[0.99] ${
                isPresent
                  ? "border-white/30 bg-white/[0.07]"
                  : "border-white/[0.08] bg-white/[0.03]"
              }`}
            >
              <span
                className={`flex size-7 shrink-0 items-center justify-center rounded-md border-2 ${
                  isPresent
                    ? "border-white bg-white text-primaryDarkBlue"
                    : "border-white/20"
                }`}
              >
                {isPresent && (
                  <svg
                    className="size-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={3}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                )}
              </span>
              <span
                className={`flex-1 text-base ${
                  isPresent ? "text-white" : "text-white/70"
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bc-fade-up mx-6 mb-3 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-center text-sm font-medium text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 px-6 pb-10">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 font-oswald text-base font-medium uppercase tracking-wider text-white/60 transition-all active:scale-[0.98] disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          aria-label="Check Out"
          onClick={handleCheckOut}
          disabled={isSubmitting}
          className="rounded-xl bg-amber-500 p-4 font-oswald text-base font-medium uppercase tracking-wider text-primaryDarkBlue shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] disabled:opacity-40"
        >
          {isSubmitting ? "Checking out..." : "Check Out"}
        </button>
      </div>

      {pendingMissing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm">
          <div className="bc-fade-up w-full max-w-sm overflow-hidden rounded-2xl border border-white/[0.08] bg-primaryBlueShade">
            <div className="border-b border-amber-500/20 bg-amber-500/[0.06] px-6 py-5 text-center">
              <h3 className="font-oswald text-xl font-medium uppercase tracking-wide text-white">
                Confirm Missing Items
              </h3>
              <p className="mt-2 text-sm text-white/60">
                {pendingMissing.length} item
                {pendingMissing.length === 1 ? "" : "s"} will be reported
                missing. Continue?
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 p-5">
              <button
                type="button"
                onClick={handleBackFromConfirm}
                disabled={isSubmitting}
                className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm font-semibold text-white/60 transition-all active:scale-[0.97]"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmMissing}
                disabled={isSubmitting}
                className="rounded-lg bg-amber-500 px-4 py-3 text-sm font-bold text-primaryDarkBlue transition-all active:scale-[0.97] disabled:opacity-50"
              >
                {isSubmitting ? "Processing..." : "Yes, check out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryStep;
